import { IDestroyOptions, Sprite, TextStyle, Text } from "pixi.js";
import { BaseScalableView } from "../../../../common/BaseScalableView";
import { PromiseCompletionSource } from "../../../../../../shared/async/PromiseCompletionSource";
import { ICanvasScaler } from "../../../../../../canvas/ICanvasScaler";
import { SocialCommunitiesResourcePackage } from "../../resources/SocialCommunitiesResourcePackage";
import { GameFont } from "../../../../common/GameFont";
import { FadeScreen } from "../../../../game/components/FadeScreen";
import { IFocusable } from "../../../../layers/IFocusable";
import { CommonResourcesPackage } from "../../../../common/resources/CommonResourcesPackage";
import { DefaultButtonWithText } from "../../../../common/components/DefaultButtonWithText";
import { gsap } from "gsap";
import { DefaultButton } from "../../../../common/components/DefaultButton";
import { SocialIconsBackground } from "../components/SocialIconsBackground";

export class SocialCommunitiesIntroPopupView extends BaseScalableView<void> implements IFocusable {
    public readonly focusable: boolean = true;
    private static readonly JOIN_TEXT = "JOIN OUR";
    private static readonly COOL_TEXT = "COOL!";
    private static readonly HAND_SCALE = 0.51;

    private readonly commonResources: CommonResourcesPackage;
    private readonly socialResourcePackage: SocialCommunitiesResourcePackage;
    private iconsBg: SocialIconsBackground;
    private logo: Sprite;
    private crownIcon: Sprite;
    private joinText: Text;
    private coolButton: DefaultButtonWithText;
    private menuButton: DefaultButton;
    private fader: FadeScreen;
    private topLight: Sprite;
    private leftLight: Sprite;
    private rightLight: Sprite;
    private middleLight: Sprite;
    private bottomLight: Sprite;
    private hand: Sprite;

    public constructor(
        operationControls: PromiseCompletionSource<void>,
        commonResources: CommonResourcesPackage,
        socialResourcePackage: SocialCommunitiesResourcePackage,
        canvasScaler: ICanvasScaler
    ) {
        super(operationControls, canvasScaler);
        this.commonResources = commonResources;
        this.socialResourcePackage = socialResourcePackage;
        this.build();
        this.layout(this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
    }

    public async start(): Promise<void> {
        this.fader.fadeIn();
        const showAnimation = new gsap.core.Timeline();
        showAnimation.fromTo(this, { alpha: 0 }, { alpha: 1, duration: 0.15, ease: "power1.in" });
        await showAnimation.play();
        showAnimation.kill();
    }

    private build(): void {
        this.fader = new FadeScreen(0.3, 0, 0.7, this.canvasScaler.referenceWidth, this.canvasScaler.referenceHeight);
        this.fader.eventMode = "static";
        this.addChild(this.fader);
        this.topLight = new Sprite(this.socialResourcePackage.assets_views_social_communities_lights_light_1_png);
        this.addChild(this.topLight);
        this.leftLight = new Sprite(this.socialResourcePackage.assets_views_social_communities_lights_light_2_png);
        this.addChild(this.leftLight);
        this.rightLight = new Sprite(this.socialResourcePackage.assets_views_social_communities_lights_light_3_png);
        this.addChild(this.rightLight);
        this.middleLight = new Sprite(this.socialResourcePackage.assets_views_social_communities_lights_light_4_png);
        this.addChild(this.middleLight);
        this.bottomLight = new Sprite(this.socialResourcePackage.assets_views_social_communities_lights_light_5_png);
        this.addChild(this.bottomLight);
        this.iconsBg = new SocialIconsBackground(this.socialResourcePackage);
        this.addChild(this.iconsBg);
        this.logo = new Sprite(this.socialResourcePackage.assets_views_social_communities_logo_png);
        this.addChild(this.logo);
        this.crownIcon = new Sprite(this.socialResourcePackage.assets_views_social_communities_bg_icons_crown_png);
        this.addChild(this.crownIcon);
        this.joinText = new Text(SocialCommunitiesIntroPopupView.JOIN_TEXT, new JoinTextTextStyle());
        this.joinText.skew._x = -0.087;
        this.joinText.angle = -5;
        this.addChild(this.joinText);
        this.coolButton = new DefaultButtonWithText(
            this.commonResources.assets_common_ui_button_green_png,
            SocialCommunitiesIntroPopupView.COOL_TEXT,
            new CoolTextTextStyle()
        );
        this.coolButton.pivot.set(this.coolButton.width * 0.5, this.coolButton.height * 0.5);
        this.coolButton.onUp.add(this.coolButtonClickHandle, this);
        this.addChild(this.coolButton);
        this.menuButton = new DefaultButton(this.socialResourcePackage.assets_views_social_communities_menu_png);
        this.addChild(this.menuButton);
        this.hand = new Sprite(this.commonResources.assets_common_ui_hand_hand_png);
        this.hand.scale.set(SocialCommunitiesIntroPopupView.HAND_SCALE);
        this.hand.angle = 83;
        this.addChild(this.hand);
    }

    private async coolButtonClickHandle(): Promise<void> {
        this.coolButton.eventMode = "none";
        const buttonAnimation = new gsap.core.Timeline();
        buttonAnimation.fromTo(this.coolButton.scale, { x: 1, y: 1 }, { x: 0.9, y: 0.9, repeat: 1, yoyo: true, duration: 0.1 });
        await buttonAnimation.play();
        buttonAnimation.kill();
        await this.hideView();
        this.operationControls.complete();
        this.coolButton.eventMode = "static";
    }

    private async hideView(): Promise<void> {
        const hideAnimation = new gsap.core.Timeline();
        hideAnimation.to(this, { alpha: 0, duration: 0.3, ease: "power1.out" });
        await hideAnimation.play();
        hideAnimation.kill();
    }

    protected layout(width: number, height: number): void {
        this.fader.width = width;
        this.fader.height = height;
        this.leftLight.position.set(0, 275);
        this.rightLight.position.set(545, 275);
        this.middleLight.position.set(0, 929);
        this.bottomLight.position.set(0, 1470);
        this.logo.position.set(width * 0.5 - this.logo.width * 0.5, 646);
        this.crownIcon.position.set(275, 439);
        this.joinText.position.set(229, 565);
        this.coolButton.position.set(width * 0.5, 1175 + this.coolButton.height * 0.5);
        this.menuButton.position.set(0, 60);
        this.hand.position.set(551, 132);
    }

    public destroy(options?: boolean | IDestroyOptions): void {
        this.coolButton.onUp.remove(this.coolButtonClickHandle, this);
        super.destroy(options);
    }
}

class JoinTextTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#FFFFFF",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 130,
        }));
    }
}

class CoolTextTextStyle extends TextStyle {
    public constructor() {
        super(new TextStyle({
            align: "center",
            fill: "#FFFFFF",
            fontFamily: GameFont.DEFAULT_FONT,
            fontSize: 78,
            dropShadow: true,
            dropShadowAngle: 0.5,
            dropShadowBlur: 5,
            dropShadowDistance: 3,
            dropShadowAlpha: 0.5
        }));
    }
}
